import React from 'react';
import PropTypes from 'prop-types';
import { Button, Typography } from '@material-ui/core';
import { AddCircleOutline } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import CustomTable from '../../components/CustomTable';

const useStyles = makeStyles((theme) => ({
  cella: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  // I nomi dei Controlli sono testo libero — `NomeControllo` li assembla lato
  // server da `categoriaPersonalizzata.nome`, `descrizioneOsservabile` o il campo
  // Descrizione — e quanti ne porti un'attrezzatura lo decide il manuale del
  // cliente: la cella non puo' dipendere ne' da quanti sono ne' da quanto sono
  // lunghi. `rt-td` impone `white-space: nowrap`, quindi senza `normal` non
  // esistono righe successive e il testo viene tagliato dal bordo della colonna,
  // a meta' parola e senza puntini: niente dice che manca qualcosa.
  //
  // Il tetto in altezza e' la meta' che distingue questa soluzione dal semplice
  // andare a capo: senza, un'attrezzatura con dodici Controlli fa una riga alta
  // mezzo schermo e la tabella smette di essere scansionabile.
  //
  // 50px sono due righe e mezzo, ed e' dispari apposta: la mezza riga tagliata e'
  // l'unico indizio di scorrevolezza disponibile, visto che su macOS la barra
  // compare solo mentre si scorre. Con 40px il contenuto sembrerebbe completo.
  controlli: {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    maxHeight: 50,
    overflowY: 'auto',
    width: '100%',
  },
  tuttoSorvegliato: {
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
    padding: theme.spacing(2),
  },
}));

/**
 * The negative half of the screen: what nothing is watching. An equipment shows
 * up here when it has at least one assignable Controllo and no live Assegnazione
 * — a suspended one does not count, because during a Sospensione the Controllo is
 * back to manual (ADR-0010).
 */
const AttrezzatureSenzaSondaTable = ({
  attrezzature,
  errore,
  loading,
  onAssocia,
}) => {
  const classes = useStyles();

  const columns = [
    {
      Header: 'Attrezzatura',
      id: 'attrezzatura',
      minWidth: 180,
      accessor: (riga) => riga.attrezzaturaNome,
      Cell: ({ original }) => (
        <div className={classes.cella}>
          <Typography variant="body2">{original.attrezzaturaNome}</Typography>
        </div>
      ),
    },
    {
      Header: 'Reparto',
      id: 'reparto',
      minWidth: 130,
      accessor: (riga) => riga.repartoNome,
      Cell: ({ original }) => (
        <div className={classes.cella}>
          <Typography variant="body2">{original.repartoNome}</Typography>
        </div>
      ),
    },
    {
      Header: 'Controlli assegnabili',
      id: 'controlliAssegnabili',
      minWidth: 280,
      accessor: (riga) => riga.controlliAssegnabili.join(', '),
      Cell: ({ original }) => (
        <div className={classes.cella}>
          {/* `tabIndex` sul contenitore scorrevole: senza, chi non usa il mouse
              non raggiunge quello che sta oltre le due righe e mezzo. */}
          <Typography
            variant="body2"
            className={classes.controlli}
            component="div"
            tabIndex={0}
            aria-label={`Controlli assegnabili di ${original.attrezzaturaNome}`}
          >
            {original.controlliAssegnabili.join(', ')}
          </Typography>
        </div>
      ),
    },
    {
      Header: 'Azioni',
      id: 'azioni',
      width: 220,
      sortable: false,
      accessor: (riga) => riga,
      Cell: ({ original }) => (
        <div className={classes.cella}>
          <Button
            size="small"
            color="primary"
            variant="outlined"
            startIcon={<AddCircleOutline />}
            onClick={() => onAssocia(original)}
          >
            Associa una sonda
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <CustomTable
        records={attrezzature}
        columns={columns}
        loading={loading}
        filterable={false}
        resizable={true}
        sortable={true}
        defaultPageSize={5}
      />
      {/* «Tutto sorvegliato» e' un'affermazione, non un vuoto: si dice solo
          quando l'elenco delle attrezzature e' davvero arrivato. */}
      {!loading && errore && (
        <Typography
          variant="body2"
          component="p"
          className={classes.tuttoSorvegliato}
        >
          {errore}
        </Typography>
      )}
      {!loading && !errore && attrezzature.length === 0 && (
        <Typography
          variant="body2"
          component="p"
          className={classes.tuttoSorvegliato}
        >
          Ogni attrezzatura con controlli assegnabili ha una sonda che la
          sorveglia adesso.
        </Typography>
      )}
    </>
  );
};

AttrezzatureSenzaSondaTable.propTypes = {
  attrezzature: PropTypes.array.isRequired,
  errore: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  onAssocia: PropTypes.func.isRequired,
};

export default AttrezzatureSenzaSondaTable;
